import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Grid, Popover } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/en';  // Ensure locale is available for formatting
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedDate } from '../../store/slice/Ticketing.slice';  // Adjust import as per your project structure
import { useTheme } from '@emotion/react';

const CalendarTicket = ({ isOpen, handleClose, Ref }) => {
  const theme = useTheme();

  const { selectedDate, StdDates,languageWords } = useSelector((store) => store?.Ticketing);
  const daysOfWeek = [
    languageWords?.monday?.slice(0, 2),
    languageWords?.Tuesday?.slice(0, 2),
    languageWords?.Wednesday?.slice(0, 2),
    languageWords?.Thursday?.slice(0, 2),
    languageWords?.Friday?.slice(0, 2),
    languageWords?.Saturday?.slice(0, 2),
    languageWords?.Sunday?.slice(0, 2),
  ];
  const dispatch = useDispatch();
  const today = dayjs(); // Get the current date
  const [currentDate, setCurrentDate] = useState(today); // State to track the current month
  const [selectedDateState, setSelectedDateState] = useState(dayjs(selectedDate)); // State to track the selected date
  const allowedDates = StdDates?.map((date) => dayjs(date));

  const shouldDisableDate = (date) => {
    return !allowedDates.some((allowedDate) => allowedDate?.isSame(date, "day"));
  };
  const getTranslatedMonth = () => {
    const monthName = currentDate?.format("MMMM");
    return languageWords?.[monthName] || monthName;
  };

  const isPastDay = (day) => currentDate.date(day).isBefore(today, 'day');
  const isBlockedDate = (day) => !allowedDates.some((allowedDate) => allowedDate?.isSame(currentDate.date(day), "day"));
  const isToday = (day) => today.isSame(currentDate.date(day), 'day');
  const isSelected = (day) => selectedDateState && selectedDateState.isSame(currentDate.date(day), 'day');

  useEffect(() => {
    setSelectedDateState(dayjs(selectedDate));
  }, [selectedDate]);

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const handleDateSelect = (day) => {
    if (!isPastDay(day) && !isBlockedDate(day)) {
      const newSelectedDate = currentDate.date(day);
      setSelectedDateState(newSelectedDate); // Set the selected date
      const formattedDate = newSelectedDate.format('YYYY-MM-DD');
      dispatch(updateSelectedDate(formattedDate)); // Dispatch selected date
      handleClose();
    }
  };

  return (
    <Popover
      open={isOpen}
      anchorEl={Ref}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Box
        sx={{
          width: { md: '430px', sm: '100%', xs: '100%' },
          mx: { sm: '1rem' },
          textAlign: 'center',
          backgroundColor: theme?.calender?.bgColor,
          px: 2,
          paddingBottom: 2,
        }}
      >
        {/* Header with arrows and year/month */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
          <IconButton onClick={handlePrevMonth} 
            sx={{
              color: theme?.calender?.arrowColor,
              "&:hover": {
                color: theme?.guest?.arrowColorHover,
              },
              "&:active": {
                color: theme?.calender?.arrowColorActive,
              },
              "&.Mui-disabled": {
                color: theme?.calender?.disabledArrowColor,
              },
            }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {getTranslatedMonth()}
          </Typography>
          <IconButton onClick={handleNextMonth}
            sx={{
              color: theme?.calender?.arrowColor,
              "&:hover": {
                color: theme?.guest?.arrowColorHover,
              },
              "&:active": {
                color: theme?.calender?.arrowColorActive,
              },
              "&.Mui-disabled": {
                color: theme?.calender?.disabledArrowColor,
              },
            }}>
            <ArrowForward />
          </IconButton>
        </Box>

        {/* Display Year and Month */}
        <Typography
          sx={{
            marginBottom: 1,
            color: theme?.calender?.monthTextColor,
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          {getTranslatedMonth()}
        </Typography>

        {/* Days of the week */}
        <Grid container spacing={1} mt={3}>
          {daysOfWeek.map((day, index) => (
            <Grid item xs={1.71} key={index}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme?.calender?.daysTextColor }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Dates Grid */}
        <Grid container spacing={1} sx={{ marginTop: 1 }}>
          {/* Empty cells for days before the first day of the month */}
          {[...Array(currentDate.startOf('month').day())].map((_, index) => (
            <Grid item xs={1.71} key={index} />
          ))}

          {/* Render all days of the month */}
          {[...Array(currentDate.daysInMonth())].map((_, dayIndex) => {
            const day = dayIndex + 1;
            const isBlocked = isBlockedDate(day);
            const isSelectedDay = isSelected(day);
            const isActive = !isPastDay(day) && !isBlocked;

            return (
              <Grid item xs={1.71} key={dayIndex}>
                <Box
                  onClick={() => isActive && handleDateSelect(day)} // Only allow selection if active
                  sx={{
                    border: '2px dotted',
                    borderColor: isBlocked || isPastDay(day)
                      ? theme?.calender?.inActiveBorderColor
                      : theme?.calender?.dateBorerColor,
                    color: isSelectedDay
                      ? theme?.calender?.dateTextColorActive
                      : isBlocked || isPastDay(day)
                      ? theme?.calender?.inActiveTextColor
                      : theme?.calender?.dateTextColor,
                    backgroundColor: isSelectedDay ? theme?.calender?.dateBgColorActive : '',
                    cursor: isActive ? 'pointer' : 'default',
                    borderRadius: '8px',
                    padding: 1,
                    textAlign: 'center',
                    transition: 'background-color 0.2s ease',
                    width: '40px',
                    height: '40px',
                    fontWeight: isActive ? 'bold' : 'normal',
                    lineHeight: '1',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: isSelectedDay
                        ? theme?.calender?.dateTextColorActive
                        : isBlocked || isPastDay(day)
                        ? "#969BA0"
                        : isActive
                        ? '#3E4954'
                        : theme?.calender?.dateTextColor,
                      fontSize: '22px',
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Popover>
  );
};

export default CalendarTicket;
